/**
 * useAnnotationSync - Syncs annotation creation/deletion with remote updates via Pusher
 *
 * This hook:
 * 1. Listens for granular Pusher events when annotations are created/deleted by other users
 * 2. Updates the Apollo cache directly (no full project refetch)
 * 3. The existing syncFromGraphQL pipeline picks up cache changes automatically
 */

import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { subscribeToPrivateProjectChannel, clientId } from '../pusher';
import { ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables } from '../../types/graphqlTypes';
import { PROJECT_ANNOTATION_STATE } from '../../graphql/project';
import { PropertyType, PropertyValueType, PropertyValueData } from '@pointorama/potree-shared';
import { getGlobalPropertyService } from '@pointorama/potree-shared';
import { buildAnnotationCacheEntry } from './annotationCacheUtils';
import type { Channel } from 'pusher-js';

/** Server event payload for annotation creation */
interface AnnotationCreatedEvent {
  annotation: {
    identifier: string;
    type: string;
    groupIdentifier?: string | null;
  };
  orderedIdentifier: {
    identifier: string;
    index: number;
  };
  initialProperties: Array<{
    identifier: string;
    propertyIdentifier: string;
    value: unknown;
    valueType: string;
    propertyType: string;
  }>;
  executedBy: string;
  clientId?: string;
}

/** Server event payload for annotation deletion */
interface AnnotationsDeletedEvent {
  identifiers: string[];
  executedBy: string;
  clientId?: string;
}

export interface UseAnnotationSyncOptions {
  projectId: string;
}

/**
 * Hook that syncs annotation creation/deletion with remote Pusher updates.
 * Updates Apollo cache directly instead of refetching the entire project.
 */
export const useAnnotationSync = (options: UseAnnotationSyncOptions): void => {
  const { projectId } = options;
  const apolloClient = useApolloClient();
  const channelRef = useRef<Channel | null>(null);

  useEffect(() => {
    if (!projectId) return;

    const channel = subscribeToPrivateProjectChannel(projectId);
    channelRef.current = channel;

    const handleAnnotationCreated = (event: AnnotationCreatedEvent) => {
      // Ignore events triggered by this client — the optimistic cache write
      // already handled it. Without this, the server echo overwrites property
      // values that undo/redo may have changed since the create was dispatched.
      if (event.clientId === clientId) return;

      // Write initial properties to PropertyService so 3D objects can subscribe immediately.
      // Only write properties that don't already have a value — a client-property-updated
      // event may have arrived before this annotation-created event with newer data.
      const propertyService = getGlobalPropertyService();
      if (propertyService && event.initialProperties?.length) {
        const newProps = event.initialProperties.filter((prop) =>
          !propertyService.hasProperty(prop.identifier, prop.propertyIdentifier, prop.propertyType as PropertyType),
        );
        if (newProps.length > 0) {
          propertyService.setValuesLocally(
            newProps.map((prop) => ({
              identifier: prop.identifier,
              objectIdentifier: prop.propertyIdentifier,
              propertyType: prop.propertyType as PropertyType,
              value: prop.value as PropertyValueData,
            })),
          );
        }
      }

      // Update Apollo cache with the new annotation
      try {
        const cached = apolloClient.readQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
          query: PROJECT_ANNOTATION_STATE,
          variables: { projectId },
        });
        if (cached?.projectById) {
          const newAnnotation = buildAnnotationCacheEntry({
            identifier: event.annotation.identifier,
            type: event.annotation.type,
            groupIdentifier: event.annotation.groupIdentifier,
            initialProperties: event.initialProperties?.map((prop) => ({
              identifier: prop.identifier,
              objectIdentifier: prop.propertyIdentifier,
              value: prop.value,
              valueType: prop.valueType as PropertyValueType,
              propertyType: prop.propertyType as PropertyType,
            })),
          });

          const existingAnnotations = cached.projectById.state.annotations || [];
          const existingOrdered = cached.projectById.state.orderedIdentifiers || [];

          // Skip if annotation already exists (optimistic update from the originating tab)
          if (existingAnnotations.some((a) => a.identifier === event.annotation.identifier)) {
            return;
          }

          apolloClient.writeQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
            query: PROJECT_ANNOTATION_STATE,
            variables: { projectId },
            data: {
              ...cached,
              projectById: {
                ...cached.projectById,
                state: {
                  ...cached.projectById.state,
                  annotations: [...existingAnnotations, newAnnotation as (typeof existingAnnotations)[number]],
                  orderedIdentifiers: [
                    ...existingOrdered,
                    {
                      __typename: 'orderedIdentifier' as const,
                      identifier: event.orderedIdentifier.identifier,
                      index: event.orderedIdentifier.index,
                    },
                  ],
                },
              },
            },
          });
        }
      } catch {
        // Cache read/write failed — will be reconciled on next project fetch
      }
    };

    const handleAnnotationsDeleted = (event: AnnotationsDeletedEvent) => {
      if (event.clientId === clientId) return;

      const deletedSet = new Set(event.identifiers);

      // Update Apollo cache by removing deleted annotations
      try {
        const cached = apolloClient.readQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
          query: PROJECT_ANNOTATION_STATE,
          variables: { projectId },
        });
        if (cached?.projectById) {
          const existingAnnotations = cached.projectById.state.annotations || [];
          const existingOrdered = cached.projectById.state.orderedIdentifiers || [];

          apolloClient.writeQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
            query: PROJECT_ANNOTATION_STATE,
            variables: { projectId },
            data: {
              ...cached,
              projectById: {
                ...cached.projectById,
                state: {
                  ...cached.projectById.state,
                  annotations: existingAnnotations.filter((a) => !deletedSet.has(a.identifier)),
                  orderedIdentifiers: existingOrdered.filter((o) => !deletedSet.has(o.identifier)),
                },
              },
            },
          });
        }
      } catch {
        // Cache read/write failed — will be reconciled on next project fetch
      }
    };

    channel.bind('annotation-created', handleAnnotationCreated);
    channel.bind('annotations-deleted', handleAnnotationsDeleted);

    return () => {
      // Only unbind our handlers — don't unsubscribe the channel
      // (other hooks share the same private channel subscription)
      channel.unbind('annotation-created', handleAnnotationCreated);
      channel.unbind('annotations-deleted', handleAnnotationsDeleted);
      channelRef.current = null;
    };
  }, [projectId, apolloClient]);
};
